import { useContext, useLayoutEffect, useRef, useState } from "react";
import { AccountSettingsModal } from "./AccountSettingsModal";
import { BreadAccount } from "breadcommon";
import { DollarAmount } from "../common/DollarAmount";
import { AccountIdentity } from "../common/AccountIdentity";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "../rye/RyeButton";
import { AccountSyncStatus } from "./AccountSyncStatus";
import { useRyeModal } from "../rye/RyeModal";
import { RyeInput } from "../rye/RyeInput";
import { RyeAnimatedHeight } from "../rye/RyeAnimatedHeight";
import { animateRyeSlide } from "../rye/motion";
import { UserContext } from "../firebaseio/UserContext";
import { firestoreUpdateAccount } from "../firebaseio/firestoreIo";
import { manualAccountName } from "./accountName";

export function AccountRow(props: { account: BreadAccount }): JSX.Element {
  const { openModal } = useRyeModal();
  const user = useContext(UserContext);
  const [isEditingName, setIsEditingName] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);
  const hasEdited = useRef(false);

  // The name glides right into the input's padding as editing starts, and
  // back as it ends. Nothing moves on first render.
  useLayoutEffect(() => {
    if (!isEditingName && !hasEdited.current) return;
    hasEdited.current = true;
    if (isEditingName) {
      animateRyeSlide(nameRef.current?.querySelector("input"), [
        { paddingLeft: "0px" },
        { paddingLeft: "12px" },
      ]);
    } else {
      animateRyeSlide(nameRef.current?.querySelector("button"), [
        { transform: "translateX(13px)" },
        { transform: "translateX(0)" },
      ]);
    }
  }, [isEditingName]);

  const isRenamed = props.account.name !== props.account.original_name;

  // What's typed while renaming. Enter, Esc, clicking away and Save all keep
  // it; only Cancel throws it away.
  // A ref, not state, so a blur fired as the input is removed after Cancel
  // can't save.
  const draftName = useRef(props.account.name);
  const nameEditOpen = useRef(false);
  function startEditingName() {
    draftName.current = props.account.name;
    nameEditOpen.current = true;
    setIsEditingName(true);
  }
  function finishEditingName(keep: boolean) {
    if (!nameEditOpen.current) return;
    nameEditOpen.current = false;
    if (keep) rename(draftName.current);
    setIsEditingName(false);
  }

  function rename(newName: string) {
    const manualName = manualAccountName(newName, props.account.original_name);
    if ((manualName ?? props.account.original_name) === props.account.name) {
      return;
    }
    if (user) {
      firestoreUpdateAccount(user.uid, props.account.id, {
        manual_data: { name: manualName },
      });
    }
  }

  return (
    <div
      className={buildClasses(
        "w-full",
        "flex",
        "items-center",
        "gap-4",
        "py-3",
        "pl-1",
        "group/accountrow",
      )}
    >
      <AccountIdentity
        account={props.account}
        name={
          // Slides open to a full-size input while renaming, pushing the
          // details line down rather than crowding it.
          <div ref={nameRef}>
            <RyeAnimatedHeight>
              {isEditingName ? (
                <div className={buildClasses("pb-1")}>
                  <RyeInput
                    startingText={props.account.name}
                    width="full"
                    focusOnInit={true}
                    onChange={(text) => (draftName.current = text)}
                    onEnter={() => finishEditingName(true)}
                    onEsc={() => finishEditingName(true)}
                    onBlur={() => finishEditingName(true)}
                    trailing={
                      // Keep focus in the input while pressing these, so it
                      // doesn't blur (and save) before Cancel can discard.
                      <div
                        className={buildClasses("flex", "gap-0.5")}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <RyeButton
                          icon="close"
                          size="sm"
                          variant="transparent"
                          vibe="subdued"
                          tooltip="Cancel"
                          onClick={() => finishEditingName(false)}
                        />
                        <RyeButton
                          icon="check"
                          size="sm"
                          variant="transparent"
                          vibe="subdued"
                          iconColor="purple"
                          tooltip="Save"
                          onClick={() => finishEditingName(true)}
                        />
                      </div>
                    }
                  />
                </div>
              ) : (
                <div className={buildClasses("flex", "items-center", "gap-1")}>
                  <button
                    className={buildClasses(
                      "min-w-0",
                      "truncate",
                      "text-left",
                      "cursor-text",
                      "outline-none",
                    )}
                    onClick={startEditingName}
                  >
                    {props.account.name}
                  </button>
                  <div
                    className={buildClasses(
                      "flex",
                      "flex-shrink-0",
                      ...REVEAL_ON_ROW_HOVER,
                    )}
                  >
                    <RyeButton
                      icon="edit"
                      size="sm"
                      variant="transparent"
                      vibe="subdued"
                      tooltip="Rename"
                      onClick={startEditingName}
                    />
                    {isRenamed ? (
                      <RyeButton
                        icon="undo"
                        size="sm"
                        variant="transparent"
                        vibe="subdued"
                        tooltip={`Reset to original synced name: ${props.account.original_name}`}
                        tooltipContent={
                          <>
                            {"Reset to original synced name: "}
                            <i>{props.account.original_name}</i>
                          </>
                        }
                        onClick={() => rename(props.account.original_name)}
                      />
                    ) : null}
                  </div>
                </div>
              )}
            </RyeAnimatedHeight>
          </div>
        }
      />
      <div className={buildClasses("w-48", "flex-shrink-0", "flex")}>
        <AccountSyncStatus account={props.account} />
      </div>
      <div
        className={buildClasses("w-32", "flex-shrink-0", "flex", "justify-end")}
      >
        <DollarAmount
          n={props.account.current_balance ? props.account.current_balance : 0}
          includeDecimals={false}
          showPlus={false}
          showMinus={true}
          showDollarSign={true}
          align="right"
          currencyCode={props.account.currency_code}
        />
      </div>
      <RyeButton
        icon="settings"
        size="sm"
        variant="transparent"
        vibe="subdued"
        tooltip="Account settings"
        onClick={() => {
          openModal(<AccountSettingsModal initialAccount={props.account} />);
        }}
      />
    </div>
  );
}

/** Row controls that only show while the row is hovered or has focus. */
const REVEAL_ON_ROW_HOVER = [
  "opacity-0",
  "group-hover/accountrow:opacity-100",
  "focus-within:opacity-100",
  "transition-opacity",
];
